/** Schemes we can complete via Composio-managed OAuth (`session.authorize`). */
export const COMPOSIO_MANAGED_OAUTH_SCHEMES = new Set(['OAUTH2', 'OAUTH1']);

/** Schemes we can complete by pasting credentials into `connectedAccounts.initiate`. */
export const KEY_BASED_AUTH_SCHEMES = new Set([
  'API_KEY',
  'BEARER_TOKEN',
  'BASIC',
  'BASIC_WITH_JWT',
]);

export type ConnectableAuthSchemeKind = 'oauth' | 'api_key';

export type ConnectableAuthSchemeOption = {
  readonly authScheme: string;
  readonly kind: ConnectableAuthSchemeKind;
};

/**
 * Whether a toolkit exposes at least one auth path the app can complete.
 * @param authSchemes All schemes the toolkit advertises.
 * @param composioManagedAuthSchemes Schemes Composio manages (OAuth apps).
 * @returns True when OAuth-managed or key-based auth is available.
 */
export function isToolkitConnectable(
  authSchemes: readonly string[] | undefined,
  composioManagedAuthSchemes: readonly string[] | undefined,
): boolean {
  const managed = (composioManagedAuthSchemes ?? []).map((scheme) => scheme.toUpperCase());
  if (managed.some((scheme) => COMPOSIO_MANAGED_OAUTH_SCHEMES.has(scheme))) {
    return true;
  }

  const all = (authSchemes ?? []).map((scheme) => scheme.toUpperCase());
  return all.some((scheme) => KEY_BASED_AUTH_SCHEMES.has(scheme));
}

/**
 * Builds the ordered list of connectable schemes for the authenticate step.
 * Composio-managed OAuth first (default), then key-based schemes.
 * @param authSchemes All advertised toolkit schemes (used for key-based options).
 * @param composioManagedAuthSchemes Schemes Composio manages as OAuth apps.
 * @returns Deduped scheme options in display order.
 */
export function buildConnectableAuthSchemes(
  authSchemes: readonly string[] | undefined,
  composioManagedAuthSchemes: readonly string[] | undefined,
): readonly ConnectableAuthSchemeOption[] {
  const options: ConnectableAuthSchemeOption[] = [];
  const seen = new Set<string>();

  for (const raw of composioManagedAuthSchemes ?? []) {
    const authScheme = raw.toUpperCase();
    if (!COMPOSIO_MANAGED_OAUTH_SCHEMES.has(authScheme) || seen.has(authScheme)) continue;
    seen.add(authScheme);
    options.push({ authScheme, kind: 'oauth' });
  }

  for (const raw of authSchemes ?? []) {
    const authScheme = raw.toUpperCase();
    if (!KEY_BASED_AUTH_SCHEMES.has(authScheme) || seen.has(authScheme)) continue;
    seen.add(authScheme);
    options.push({ authScheme, kind: 'api_key' });
  }

  return options;
}

/**
 * User-facing i18n key for labelling a connectable auth scheme in the dialog.
 * @param authScheme Composio auth scheme enum value (e.g. OAUTH2, API_KEY).
 * @returns Key under the `connection-ui` namespace.
 */
export function schemeLabelKeyForAuthScheme(authScheme: string): string {
  const upper = authScheme.toUpperCase();
  if (COMPOSIO_MANAGED_OAUTH_SCHEMES.has(upper)) return 'schemeSignInWith';
  if (upper === 'BEARER_TOKEN') return 'schemeUseAccessToken';
  return 'schemeUseApiKey';
}

/**
 * Display name tweak for known slug collisions (Slack user vs Slack bot).
 * @param slug Composio toolkit identifier used to detect Slack variants.
 * @param name Human-readable toolkit title from the Composio catalog.
 * @returns Label shown in the choose/authenticate UI.
 */
export function displayNameForToolkit(slug: string, name: string): string {
  const key = slug.trim().toLowerCase();
  if (key === 'slackbot') return `${name} (bot)`;
  if (key === 'slack') return `${name} (user)`;
  return name;
}

/**
 * Heuristic for masking initiation fields when Composio strips `is_secret`.
 * @param fieldName Field name from initiation fields.
 * @param fieldType Field type string when present.
 * @returns True when the input should use `type="password"`.
 */
export function isSecretInitiationField(fieldName: string, fieldType?: string): boolean {
  const type = (fieldType ?? '').toLowerCase();
  if (type === 'password' || type === 'secret') return true;
  return /token|key|secret|password|bearer|credential/i.test(fieldName);
}
